<template>
    <div class="campaigns">
        <div class="page-header">
            <h1 class="page-title">{{ $t("messages.campaigns") }}</h1>
            <button v-if="$store.state.user.roleId !== 3" class="create-button" @click="goToCreatePage">
                <img src="/images/plus.svg" alt="create">
                {{ $t("messages.create") }}
            </button>
        </div>

        <div class="content">
            <div class="filters">
                <div class="filter-tab" :class="{ active: activeFilter === 'active' }"
                    @click="setActiveFilter('active')">
                    {{ $t("messages.active_campaigns") }}
                    <span class="filter-count">{{ getCampaignCount('active') }}</span>
                </div>
                <div class="filter-tab" :class="{ active: activeFilter === 'paused' }"
                    @click="setActiveFilter('paused')">
                    {{ $t("messages.paused_campaigns") }}
                    <span class="filter-count">{{ getCampaignCount('paused') }}</span>
                </div>
                <div class="filter-tab" :class="{ active: activeFilter === 'pending' }"
                    @click="setActiveFilter('pending')">
                    {{ $t("messages.pending_campaigns") }}
                    <span class="filter-count">{{ getCampaignCount('pending') }}</span>
                </div>
                <div class="filter-tab" :class="{ active: activeFilter === 'completed' }"
                    @click="setActiveFilter('completed')">
                    {{ $t("messages.completed_campaigns") }}
                    <span class="filter-count">{{ getCampaignCount('completed') }}</span>
                </div>
            </div>

            <table class="campaigns-table table">
                <thead>
                    <tr>
                        <th class="campaign-name name">{{ $t("messages.campaign_name") }}</th>
                        <th class="adgroups-col" title="Ad Groups"></th>
                        <th class="status">{{ $t("messages.display_status") }}</th>
                        <th class="period">{{ $t("messages.period") }}</th>
                        <th class="impressions">
                            <div class="sortable">
                                Impressions
                                <img src="/images/up-down.svg" alt="sort" class="sort-icon">
                            </div>
                        </th>
                        <th class="clicks">
                            <div class="sortable">
                                Clicks
                                <img src="/images/up-down.svg" alt="sort" class="sort-icon">
                            </div>
                        </th>
                        <th class="views">
                            <div class="sortable">
                                Views
                                <img src="/images/up-down.svg" alt="sort" class="sort-icon">
                            </div>
                        </th>
                        <th class="revenue">
                            {{ $t("messages.total_budget") }}
                        </th>
                        <th class="cpm">CPM</th>
                        <th class="cpc">CPC</th>
                        <th class="cpv">CPV</th>
                        <th class="actions-col"></th>
                    </tr>
                </thead>
                <tbody>

                    <template v-for="row in filteredCampaigns" :key="row.id">
                        <tr>
                            <td class="campaign-name name">
                                <div class="name-content">
                                    <span @click="editCampaign(row.id)" class="title clickable">
                                        {{ row.campaign }}
                                    </span>
                                    <span v-if="row.is_test" class="status-badge status-test">{{ $t('messages.test_campaign_badge') }}</span>
                                </div>
                            </td>
                            <td class="adgroups">
                                <!-- Always show plus to create ad group. If campaign has adGroups also show toggle arrow -->
                                <div class="adgroups-actions">
                                    <a :href="`/campaigns/${row.id}/ad-groups/create`" class="create-adgroup-btn"
                                        title="Create ad group">
                                        <img src="/images/plus-black.svg" alt="create" />
                                    </a>

                                    <button
                                        v-if="(row.adGroups && row.adGroups.length > 0) || (row.ad_groups && row.ad_groups.length > 0)"
                                        class="adgroups-toggle" @click.stop="toggleExpand(row.id)"
                                        :title="isExpanded(row.id) ? 'Collapse' : 'Expand'">
                                        <img :src="isExpanded(row.id) ? '/images/arrow-down.svg' : '/images/arrow-right.svg'"
                                            alt="toggle" />
                                    </button>
                                </div>
                            </td>
                            <td class="status">
                                <span class="status-badge" :class="getStatusClass(row.status)">
                                    {{ getStatusText(row.status) }}
                                </span>
                            </td>
                            <td class="period">
                                <div class="period-content">
                                    <div class="start-date">{{ formatDate(row.start_date) }}</div>
                                    <div class="end-date">{{ formatDate(row.end_date) }}</div>
                                </div>
                            </td>
                            <td class="impressions">
                                {{ row.impressions.toLocaleString() }}
                            </td>
                            <td class="clicks">
                                {{ row.clicks.toLocaleString() }}
                            </td>
                            <td class="views">

                                {{ row.views >= 0 && row.creative_type !== "Display" && row.creative_type !==
                                    "Audio"
                                    ? row.views.toLocaleString()
                                    : "—" }}
                            </td>
                            <td class="revenue">
                                <div class="revenue-fact">
                                    {{ formatMoneyOrDash(row, row.revenue_fact != null
                                        ? formatCurrency(row.revenue_fact, getCurrencySymbol(row.currency), 2)
                                        : kpis(row).revenue_fact) }}
                                </div>
                                <div class="planned-budget">
                                    {{ formatMoneyOrDash(row, formatCurrency(row.total_budget || row.totalBudget, getCurrencySymbol(row.currency), 2)) }}
                                </div>
                            </td>
                            <td class="cpm">—</td>
                            <td class="cpc">—</td>
                            <td class="cpv">—</td>
                            <td class="actions">
                                <button class="delete-button" @click.stop="confirmDelete(row.id)"
                                    :title="$t('messages.delete')">
                                    <img src="/images/delete.svg" alt="delete" />
                                </button>
                            </td>
                        </tr>
                        <!-- Expanded adgroups row -->
                        <tr v-if="isExpanded(row.id)" class="adgroup-row" :key="`ad-list-${row.id}`">
                            <td colspan="12" class="adgroup-row-td">
                                <table class="inner-adgroups-table">
                                    <thead>
                                        <tr>
                                            <th class="campaign-name name">{{ $t("messages.ad_group_name") }}</th>
                                            <th class="adgroups-col"></th>
                                            <th class="status">{{ $t("messages.display_status") }}</th>
                                            <th class="period">{{ $t("messages.period") }}</th>
                                            <th class="impressions">Impressions</th>
                                            <th class="clicks">Clicks</th>
                                            <th class="views">Views</th>
                                            <th class="revenue">{{ $t("messages.budget") }}</th>
                                            <th class="cpm">CPM</th>
                                            <th class="cpc">CPC</th>
                                            <th class="cpv">CPV</th>
                                            <th class="actions-col"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="g in getAdGroups(row)" :key="`ad-${g.id}`">
                                            <td class="campaign-name name">
                                                <div class="name-content">
                                                    <a :href="`/campaigns/${row.id}/ad-groups/${g.id}/edit`" class="title clickable">{{
                                                        g.name }}</a>
                                                </div>
                                            </td>
                                            <td class="adgroups-col">
                                                <a :href="`/campaigns/${row.id}/ad-groups/${g.id}/creatives`" class="attach-creatives-btn"
                                                    title="Attach creatives">
                                                    <img src="/images/plus-black.svg" alt="attach" />
                                                </a>
                                            </td>
                                            <td class="status">
                                                <span class="status-badge" :class="getStatusClass(g.status)">{{
                                                    getStatusText(g.status) }}</span>
                                            </td>
                                            <td class="period">
                                                <div class="period-content">
                                                    <div class="start-date">{{ g.start_date ? formatDate(g.start_date) :
                                                        '—' }}</div>
                                                    <div class="end-date">{{ g.end_date ? formatDate(g.end_date) : '—'
                                                        }}</div>
                                                </div>
                                            </td>
                                            <td class="impressions">{{ (g.impressions || g.impressions === 0) ?
                                                g.impressions.toLocaleString() : '—' }}</td>
                                            <td class="clicks">{{ (g.clicks || g.clicks === 0) ?
                                                g.clicks.toLocaleString() : '—' }}</td>
                                            <td class="views">{{ (g.views || g.views === 0) ? g.views.toLocaleString() :
                                                '—' }}</td>
                                            <td class="revenue">
                                                <div class="revenue-fact">
                                                    {{ formatMoneyOrDash(row, g.revenue_fact != null
                                                        ? formatCurrency(g.revenue_fact, getCurrencySymbol(g.currency || row.currency), 2)
                                                        : adgroupKpis(g).revenue_fact) }}
                                                </div>
                                                <div class="planned-budget">
                                                    {{ formatMoneyOrDash(row, formatCurrency(g.total_budget || g.totalBudget, getCurrencySymbol(g.currency || row.currency), 2)) }}
                                                </div>
                                            </td>
                                            <td class="cpm">
                                                {{ formatMoneyOrDash(row, g.cpm != null
                                                    ? formatCurrency(g.cpm, getCurrencySymbol(g.currency || row.currency), 2)
                                                    : adgroupKpis(g).cpm) }}
                                            </td>
                                            <td class="cpc">
                                                {{ formatMoneyOrDash(row, g.cpc != null
                                                    ? formatCurrency(g.cpc, getCurrencySymbol(g.currency || row.currency), 2)
                                                    : adgroupKpis(g).cpc) }}
                                            </td>
                                            <td class="cpv">
                                                {{ formatMoneyOrDash(row, g.cpv != null
                                                    ? formatCurrency(g.cpv, getCurrencySymbol(g.currency || row.currency), 2)
                                                    : adgroupKpis(g).cpv) }}
                                            </td>
                                            <td class="actions">
                                                <button class="delete-button" @click.stop="removeAdGroup(g.id)"
                                                    :title="$t('messages.delete')">
                                                    <img src="/images/delete.svg" alt="delete" />
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
export default {
    name: "Campaigns",
    props: ["campaigns", "currencies"],
    data() {
        return {
            activeFilter: 'active',
            filters: {
                campaign: { value: "", keys: ["campaign", "id"] },
                status: { value: ["active", "pending"], keys: ["status"] },
            },
            expandedCampaigns: [],
        };
    },
    computed: {
        filteredCampaigns() {
            return this.campaigns.filter(campaign => {
                switch (this.activeFilter) {
                    case 'active':
                        return campaign.status === 'active';
                    case 'paused':
                        return campaign.status === 'paused';
                    case 'pending':
                        return campaign.status === 'pending';
                    case 'completed':
                        return campaign.status === 'completed';
                    default:
                        return true;
                }
            });
        }
    },
    methods: {
        goToCreatePage() {
            window.location.href = '/campaigns/create';
        },
        setActiveFilter(filter) {
            this.activeFilter = filter;
        },
        getCampaignCount(filter) {
            return this.campaigns.filter(campaign => {
                switch (filter) {
                    case 'active':
                        return campaign.status === 'active';
                    case 'paused':
                        return campaign.status === 'paused';
                    case 'pending':
                        return campaign.status === 'pending';
                    case 'completed':
                        return campaign.status === 'completed';
                    default:
                        return true;
                }
            }).length;
        },
        editCampaign(campaignId) {
            window.location.href = `/campaigns/${campaignId}/edit`;
        },
        toggleExpand(campaignId) {
            const idx = this.expandedCampaigns.indexOf(campaignId);
            if (idx === -1) {
                this.expandedCampaigns.push(campaignId);
            } else {
                this.expandedCampaigns.splice(idx, 1);
            }
        },
        isExpanded(campaignId) {
            return this.expandedCampaigns.indexOf(campaignId) !== -1;
        },
        getAdGroups(campaign) {
            const groups = campaign.adGroups || campaign.ad_groups || [];
            return groups.map(g => {
                if ((!g.currency || g.currency === '') && campaign.currency) {
                    return { ...g, currency: campaign.currency };
                }
                return g;
            });
        },
        goToAdGroups(campaignId) {
            window.location.href = `/campaigns/${campaignId}/ad-groups`;
        },
        confirmDelete(campaignId) {
            if (!confirm(this.$t('messages.confirm_delete_campaign'))) return;
            this.deleteCampaign(campaignId);
        },
        async deleteCampaign(campaignId) {
            try {
                const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                const response = await fetch(`/campaigns/${campaignId}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': token
                    }
                });

                if (response.ok) {
                    // refresh the page to reflect deletion
                    window.location.reload();
                } else {
                    const result = await response.json();
                    throw new Error(result.message || 'Error deleting campaign');
                }
            } catch (e) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: e.message || this.$t('messages.error_occurred'),
                    type: 'error'
                });
            }
        },
        async removeAdGroup(adGroupId) {
            if (!confirm(this.$t('messages.confirm_delete'))) return;
            try {
                const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                const response = await fetch('/ad-groups/delete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': token
                    },
                    body: JSON.stringify({ id: adGroupId })
                });

                const result = await response.json().catch(() => ({}));

                if (response.ok && result.success !== false) {
                    window.location.reload();
                    return;
                }

                throw new Error(result.message || 'Error deleting ad group');
            } catch (e) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: e.message || this.$t('messages.error_occurred'),
                    type: 'error'
                });
            }
        },
        getStatusClass(status) {
            switch (status) {
                case 'active':
                    return 'status-active';
                case 'pending':
                    return 'status-pending';
                case 'completed':
                    return 'status-completed';
                case 'paused':
                    return 'status-paused';
                default:
                    return 'status-default';
            }
        },
        getStatusText(status) {
            switch (status) {
                case 'active':
                    return this.$t("messages.active_status");
                case 'paused':
                    return this.$t("messages.paused_status");
                case 'pending':
                    return this.$t("messages.pending_status");
                case 'completed':
                    return this.$t("messages.completed_status");
                default:
                    return status;
            }
        },
        formatCurrency(amount, currency, round) {
            if (isNaN(amount) || !isFinite(amount)) {
                return "—";
            } else if (amount === null || amount == 0) {
                return 0;
            }

            // Use locale formatting for thousand separators (ru-RU uses non-breaking spaces)
            const opts = { minimumFractionDigits: round, maximumFractionDigits: round };
            let formattedAmount = Number(amount).toLocaleString('ru-RU', opts);
            // Replace NBSP with regular space to match requested style
            formattedAmount = formattedAmount.replace(/\u00A0/g, ' ');
            return formattedAmount + " " + currency;
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString("ru-RU");
        },
        formatMoneyOrDash(campaign, value) {
            if (campaign && campaign.is_test) {
                return "—";
            }
            return value;
        },
        getCurrencySymbol(currency) {
            if (!currency) return '';
            const found = (this.currencies || []).find((o) => o.currency === currency);
            return found ? found.symbol : currency;
        },
        adgroupKpis(item) {
            // Compute KPIs for adgroup rows based on selected price and actual stats.
            try {
                const roleId = this.$store.state.user.roleId;
                const impressions = Number(item.impressions) || 0;
                const clicks = Number(item.clicks) || 0;
                const views = Number(item.views) || 0;

                // Try to resolve price from several possible fields (price object, price_altel/price_client, fallback fixes)
                let price = 0;
                const priceObj = item.price || item.price_info || null;
                if (priceObj && typeof priceObj === 'object') {
                    price = roleId < 3 ? (priceObj.price_altel ?? priceObj.agency_price_fix ?? priceObj.price) :
                        (priceObj.price_client ?? priceObj.client_price_fix ?? priceObj.price);
                } else {
                    if (roleId < 3) {
                        price = item.price_altel ?? item.agency_price_fix ?? item.client_price_fix ?? item.price;
                    } else {
                        price = item.price_client ?? item.client_price_fix ?? item.agency_price_fix ?? item.price_altel;
                    }
                }
                price = Number(price) || 0;

                // Handle common KPI types similarly
                if (item.kpi === "CPM") {
                    const budget = (price * impressions) / 1000;
                    const revenue_fact = this.formatCurrency(budget, this.getCurrencySymbol(item.currency), 2);
                    const cpm = price ? this.formatCurrency(price, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpc = clicks > 0 ? this.formatCurrency(budget / clicks, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpv = views > 0 ? this.formatCurrency(budget / views, this.getCurrencySymbol(item.currency), 2) : "—";
                    return { revenue_fact, revenue_plan: "—", cpm, cpc, cpv };
                } else if (item.kpi === "CPC") {
                    const budget = price * clicks;
                    const revenue_fact = this.formatCurrency(budget, this.getCurrencySymbol(item.currency), 2);
                    const cpc = price ? this.formatCurrency(price, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpm = impressions > 0 ? this.formatCurrency((budget / impressions) * 1000, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpv = views > 0 ? this.formatCurrency(budget / views, this.getCurrencySymbol(item.currency), 2) : "—";
                    return { revenue_fact, revenue_plan: "—", cpm, cpc, cpv };
                } else if (item.kpi === "CPV") {
                    const budget = price * views;
                    const revenue_fact = this.formatCurrency(budget, this.getCurrencySymbol(item.currency), 2);
                    const cpv = price ? this.formatCurrency(price, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpm = impressions > 0 ? this.formatCurrency((budget / impressions) * 1000, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpc = clicks > 0 ? this.formatCurrency(budget / clicks, this.getCurrencySymbol(item.currency), 2) : "—";
                    return { revenue_fact, revenue_plan: "—", cpm, cpc, cpv };
                } else if (item.kpi === "CPE") {
                    const events = Number(item.events || item.event_count || item.engagements || item.actions || item.conversions) || 0;
                    const budget = price * events;
                    const revenue_fact = this.formatCurrency(budget, this.getCurrencySymbol(item.currency), 2);
                    const cpm = impressions > 0 ? this.formatCurrency((budget / impressions) * 1000, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpc = clicks > 0 ? this.formatCurrency(budget / clicks, this.getCurrencySymbol(item.currency), 2) : "—";
                    const cpv = views > 0 ? this.formatCurrency(budget / views, this.getCurrencySymbol(item.currency), 2) : "—";
                    return { revenue_fact, revenue_plan: "—", cpm, cpc, cpv };
                }

                // Fallback to generic kpis calculation for other KPI types
                return this.kpis(item);
            } catch (e) {
                return { revenue_fact: "—", revenue_plan: "—", cpm: "—", cpc: "—", cpv: "—" };
            }
        },
        kpis(item) {
            let revenue_fact = 0;
            let revenue_plan = 0;
            let cpm = 0;
            let cpc = 0;
            let cpv = 0;

            if (this.$store.state.user.roleId < 3) {
                switch (0) {
                    case 0:
                        if (item.kpi === "CPM") {
                            revenue_fact =
                                (item.agency_price_fix * item.impressions) /
                                1000;
                            revenue_plan =
                                (item.agency_price_fix * item.kpi_budget) /
                                1000;
                            cpm = item.agency_price_fix;
                            cpc = revenue_fact / item.clicks;
                            cpv = revenue_fact / item.views;
                        } else if (item.kpi === "CPC") {
                            revenue_fact = item.agency_price_fix * item.clicks;
                            revenue_plan =
                                item.agency_price_fix * item.kpi_budget;
                            cpm = revenue_fact / item.impressions;
                            cpc = item.agency_price_fix;
                            cpv = revenue_fact / item.views;
                        } else if (item.kpi === "CPV") {
                            revenue_fact = item.agency_price_fix * item.views;
                            revenue_plan =
                                item.agency_price_fix * item.kpi_budget;
                            cpm = revenue_fact / item.impressions;
                            cpc = revenue_fact / item.clicks;
                            cpv = item.agency_price_fix;
                        }
                        break;
                    case 1:
                        revenue_plan = item.kpi_budget;
                        revenue_fact = item.revenue;
                        cpm = (item.revenue / item.impressions) * 1000;
                        cpc = item.revenue / item.clicks;
                        cpv = item.revenue / item.views;
                        break;
                }
            } else if (this.$store.state.user.roleId > 2) {
                switch (0) {
                    case 0:
                        if (item.kpi === "CPM") {
                            revenue_plan =
                                (item.client_price_fix * item.kpi_budget) /
                                1000;
                            cpm = item.client_price_fix;
                            revenue_fact = (cpm * item.impressions) / 1000;
                            cpc = revenue_fact / item.clicks;
                            cpv = revenue_fact / item.views;
                        } else if (item.kpi === "CPC") {
                            revenue_plan =
                                item.client_price_fix * item.kpi_budget;
                            cpc = item.client_price_fix;
                            revenue_fact = cpc * item.clicks;
                            cpm = revenue_fact / item.impressions;
                            cpv = revenue_fact / item.views;
                        } else if (item.kpi === "CPV") {
                            revenue_plan =
                                item.client_price_fix * item.kpi_budget;
                            cpv = item.client_price_fix;
                            revenue_fact = cpv * item.views;
                            cpm = revenue_fact / item.impressions;
                            cpc = revenue_plan / item.clicks;
                        }
                        break;
                    case 1:
                        revenue_plan = item.kpi_budget * (1 + item.fee / 100);
                        revenue_fact = item.revenue * (1 + item.fee / 100);
                        cpm = (revenue_fact / item.impressions) * 1000;
                        cpc = revenue_fact / item.clicks;
                        cpv = revenue_fact / item.views;
                        break;
                }
            }
            revenue_fact = this.formatCurrency(
                revenue_fact,
                this.getCurrencySymbol(item.currency),
                2
            );
            revenue_plan = this.formatCurrency(
                revenue_plan,
                this.getCurrencySymbol(item.currency),
                2
            );
            cpm = this.formatCurrency(
                cpm,
                this.getCurrencySymbol(item.currency),
                2
            );
            cpc = this.formatCurrency(
                cpc,
                this.getCurrencySymbol(item.currency),
                2
            );
            cpv = this.formatCurrency(
                cpv,
                this.getCurrencySymbol(item.currency),
                2
            );

            return { revenue_fact, revenue_plan, cpm, cpc, cpv };
        },
    },
};
</script>

<style scoped>
.campaigns-table {
    table-layout: fixed;
    width: 100%;
}

.adgroup-row-td {
    padding: 0;
}

/* Column widths (header and cells) */
th.campaign-name,
td.campaign-name {
    width: 15%;
}

th.adgroups-col,
td.adgroups {
    width: 5%;
}

th.actions-col,
td.actions {
    width: 5%;

    .delete-button {
        img {
            width: 16px;
            height: 16px;
        }
    }
}

td.adgroups,
td.actions {
    .adgroups-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}

.revenue-fact {
    display: block;
}

.planned-budget {
    color: #6b6b6b;
    font-size: 0.9em;
    margin-top: 4px;
}
.status-test {
    margin-left: 8px;
    background: #f0f0f0;
    color: #666;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
}
</style>
